/**
 * Background layer motion (spec 012): where a layer's translate, zoom and
 * rotate animations put it at scene time `tMs`. Pure and a function of t
 * only, never accumulated per frame, so the state at t does not depend on the
 * frame rate or on which frames were drawn before it.
 */

import type { ParallaxLayer } from '../@types';

export interface LayerMotion {
  /** Translate offset in stage px. */
  offsetX: number;
  offsetY: number;
  /** 1 + the zoom animation's relative scale change. */
  scale: number;
  /** Rotate animation angle, degrees. */
  rotation: number;
}

/** A loop/yoyo animation's progress (0–1) at `tMs`. */
function cycleProgress(tMs: number, durationMs: number, loop: boolean, yoyo: boolean): number {
  if (!(durationMs > 0)) return 1;
  let p = Math.max(0, tMs) / durationMs;
  p = loop ? p % 1 : Math.min(p, 1);
  if (yoyo) p = p < 0.5 ? p * 2 : 2 - p * 2;
  return p;
}

/** `v` wrapped into [0, size) (unchanged when the size is unknown). */
function wrap(v: number, size: number | undefined): number {
  if (!size || !(size > 0)) return v;
  const r = ((v % size) + size) % size;
  return r === size ? 0 : r;
}

/**
 * The layer's motion at `tMs`.
 *
 * - translate: `speed × t / 1000` px along each axis of the direction
 *   (`up-left` moves on both); when `loop`, wrapped into [0, image size) by
 *   the image's natural size (`imageSize`; unwrapped when it is unknown).
 * - zoom: `1 + scale × progress`, rotate: `angle × progress`, where progress
 *   runs 0→1 over `duration` (repeating when `loop`, 0→1→0 when `yoyo`).
 *   Their `ease` is not applied (the player has always drawn them linear).
 */
export function backgroundLayerMotion(
  layer: Pick<ParallaxLayer, 'animation'>,
  tMs: number,
  imageSize?: { width: number; height: number } | null,
): LayerMotion {
  const anim = layer.animation;
  let offsetX = 0;
  let offsetY = 0;
  let scale = 1;
  let rotation = 0;

  const tr = anim?.translate;
  if (tr?.enabled && Number.isFinite(tr.speed)) {
    const dist = (tr.speed * Math.max(0, tMs)) / 1000;
    const dir = tr.direction ?? '';
    if (dir.includes('left')) offsetX -= dist;
    if (dir.includes('right')) offsetX += dist;
    if (dir.includes('up')) offsetY -= dist;
    if (dir.includes('down')) offsetY += dist;
    if (tr.loop) {
      offsetX = wrap(offsetX, imageSize?.width);
      offsetY = wrap(offsetY, imageSize?.height);
    }
  }

  const zoom = anim?.zoom;
  if (zoom?.enabled) {
    scale = 1 + (zoom.scale || 0) * cycleProgress(tMs, zoom.duration, !!zoom.loop, !!zoom.yoyo);
  }

  const rot = anim?.rotate;
  if (rot?.enabled) {
    rotation = (rot.angle || 0) * cycleProgress(tMs, rot.duration, !!rot.loop, !!rot.yoyo);
  }

  // Normalise -0 so states compare cleanly.
  return { offsetX: offsetX + 0, offsetY: offsetY + 0, scale, rotation: rotation + 0 };
}
